import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    FalconNavigatorHeader,
    ICustomerPortalService,
    Party,
    Policy,
    productHelper,
    ProductsEnum,
    Subscription,
    Types
} from "@falcon/services";
import {PluginTemplates} from "../../../../../../utils/PluginTemplates";
import FalconFooter from "../../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../../components/navigation/FalconHeader";
import {PaymentMessageBanner} from "../../../../../../components/banners/PaymentMessageBanner";
import {PaymentHistoryTable} from "../PaymentHistoryTable";
import {PaymentDaySection} from "../PaymentDaySection";
import {SubscriptionEndBanner} from "../../../../../../components/banners/SubscriptionEndBanner";
import {StorageSuccessModal} from "../../../../../../components/modals/StorageSuccessModal";
import * as moment from "moment";
import {
    EditCarMtaSuccessModalWordingGenerator
} from "../../../../annual/motor/yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";
import {RouteComponentProps} from "react-router-dom";
import {SubscriptionPaymentCardsSection} from "./SubscriptionPaymentCardsSection";

interface IPaymentDetailsPageParams {
    policyNumber: string;
}

interface IPaymentDetailsPageProps extends RouteComponentProps<IPaymentDetailsPageParams> {
    location: any;
}

interface IState {
    policies?: Policy[],
    subscription?: Subscription,
    party?: Party,
    policiesLoaded: boolean,
    subscriptionLoaded: boolean,
    partyLoaded: boolean,
    latestPolicy?: Policy,
    allPoliciesAreCancelled: boolean
}

@authorize
export class PaymentDetailsPage extends React.Component<IPaymentDetailsPageProps, IState> {

    private pluginTemplates = new PluginTemplates();
    private title: string;
    private modalContent: JSX.Element;
    private referrerJourney: string | null;
    private showSuccessPopup: string | null;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IPaymentDetailsPageProps) {
        super(props);

        this.state = {
            policiesLoaded: false,
            subscriptionLoaded: false,
            partyLoaded: false,
            allPoliciesAreCancelled: true
        };

        this.pluginTemplates.setPageTitle("Payment details");
    }

    public componentDidMount() {
        this.customerPortalService.getPolicies().subscribe((policies: Policy[]) => {

            const subscriptionPolicies: Policy[] = this.getSubscriptionTypePolicies(policies);
            const subscriptionPoliciesSorted: Policy[] = subscriptionPolicies.length === 1 ?
                subscriptionPolicies : this.getSortedSubscriptionTypePolicies(subscriptionPolicies);

            this.setState({
                policies: subscriptionPolicies,
                policiesLoaded: true,
                latestPolicy: subscriptionPoliciesSorted[0]
            });

            const allPoliciesAreCancelled = productHelper.atLeastOneActivePolicy(policies);
            this.setState({allPoliciesAreCancelled});
        });

        this.customerPortalService.getSubscription().subscribe((subscription: Subscription) => {
            this.setState({
                subscription,
                subscriptionLoaded: true
            });
        });

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output,
                partyLoaded: true
            })
        });
    }

    public render() {
        const loading: boolean = !(this.state.policiesLoaded && this.state.subscriptionLoaded && this.state.partyLoaded);

        this.referrerJourney = window.sessionStorage.getItem("referrerJourney");
        this.showSuccessPopup = window.sessionStorage.getItem("showPopup");

        if (this.referrerJourney === "mtaChangePaymentDay") {
            this.setPaymentDayChangePopup();
        } else if (this.referrerJourney === "missedPayment") {
            this.setManualPaymentSuccessPopup();
        } else if (this.referrerJourney === "chargeBackPayment") {
            this.setChargeBackPaymentSuccesfullPopup();
        } else {
            window.sessionStorage.removeItem("showPopup");
            window.sessionStorage.removeItem("referrerJourney");
        }

        return (
            <div className="dashboard payment-details">

                {this.showSuccessPopup && <StorageSuccessModal title={this.title} explanationWording={this.modalContent} anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()} explanationAnythingElseWording={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}/>}

                <FalconHeader/>

                <div className="plugin">
                    <FalconNavigatorHeader back="/dashboard/monthlySubscription" title="Payment details"/>
                    {this.state.subscription && (
                        <div className="inner-container">
                            {this.state.subscription!.allPoliciesAreCancelled ? (
                                <SubscriptionEndBanner subscription={this.state.subscription!}/>
                            ) : (
                                <PaymentMessageBanner showInfoModal={true} subscription={this.state.subscription!}/>
                            )}
                        </div>
                    )}

                    <div className="panel bg-white overflow-hidden p-0 mx-3 font-weight-extra-light">
                        <div className="inner-content pb-4">
                            {this.state.subscriptionLoaded && this.state.subscription &&
                                <PaymentDaySection allPoliciesAreCancelled={this.state.allPoliciesAreCancelled}
                                                   paymentDay={this.state.subscription.nextPaymentDate}
                                                   renewalPaymentFailed={this.state.subscription.renewalPaymentFailed}
                                                   interaction={this.state.latestPolicy?.interaction}/>}

                            {this.state.latestPolicy?.interaction && <div className="dashed-10"/>}

                            <SubscriptionPaymentCardsSection party={this.state.party}/>

                            <div className="dashed-10"/>

                            <PaymentHistoryTable
                                subscription={this.state.subscription}
                                showProduct={ProductsEnum.SUBSCRIPTION}
                                paymentPeriod={PaymentHistoryTable.PaymentPeriod.all}
                                history={this.props.history}
                                location={this.props.location}
                                match={this.props.match}/>
                        </div>
                    </div>

                    {this.pluginTemplates.renderBackLink("Back", false, "/dashboard/monthlySubscription")}
                </div>

                <FalconFooter/>

                <Overlay show={loading}/>
            </div>
        );
    }

    private includesPolicyByProductType = (policy: Policy, productType: string): boolean => {
        return policy.products.includes(productType);
    }

    private getSubscriptionTypePolicies = (policies: Policy[]): Policy[] => {
        return policies.filter((policy: Policy) =>
            this.includesPolicyByProductType(policy, ProductsEnum.MOTOR) ||
                this.includesPolicyByProductType(policy, ProductsEnum.RENTERS) ||
                    this.includesPolicyByProductType(policy, ProductsEnum.SUBSCRIPTION));
    }

    private getSortedSubscriptionTypePolicies = (subscriptionPolicies: Policy[]) => {
        return subscriptionPolicies.sort((a, b) => moment(b.endDate).diff(a.endDate));
    }

    private setPaymentDayChangePopup(): void {
        if (this.state.subscription) {
            const paymentDate: string = this.state.subscription?.nextPaymentDate!;
            const additionalPremium: string = window.sessionStorage.getItem("mtaChangePaymentDayAdditionalPremium")!;

            if (additionalPremium) {
                this.title = "Payment was successful";
                this.modalContent =
                    <p>
                        <b>£{additionalPremium} </b>
                        have been debited from your card
                        <b> for changing your payment date to {this.convertDate(paymentDate)}.</b>
                    </p>
            } else {
                this.title = "All done";
                this.modalContent =
                    <p>
                        We’ve saved your changes, and there’s no extra cost
                        <b> for changing your payment date to {this.convertDate(paymentDate)}.</b>
                    </p>
            }
        }
    }

    private setManualPaymentSuccessPopup(): void {
        this.title = "Payment was successful";
        this.modalContent =
            <p>
                <strong>£{window.sessionStorage.getItem("paymentAmount")}</strong> have been debited from your
                card <strong>to renew your subscription for 1 month</strong>
            </p>;
    }


    private setChargeBackPaymentSuccesfullPopup(): void {
        this.title = "Payment was successful";
        this.modalContent =
            <p>
                <strong>£{window.sessionStorage.getItem("paymentAmount")}</strong> have been debited from your
                card <strong>to renew your subscription for 1 month</strong>
            </p>;
    }

    private convertDate = (date: string): string => {
        return PluginTemplates.getOrdinal(parseInt(moment(date).format("DD"), 10))
    }

}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/payments/paymentDetails/subscription/PaymentDetailsPage.tsx